import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Grid,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  CalculateOutlined,
  AddCircleOutlineOutlined,
  InfoOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import { ConsommablesCalcules } from '../types';
import { CorpsMetier } from '../types/subscription';
import {
  CONSOMMABLES_PLOMBERIE,
  CONSOMMABLES_ELECTRICITE,
  CONSOMMABLES_MACONNERIE,
  TypePiecePlomberie,
  TypePieceElectricite,
  TypePieceMaconnerie,
  ConfigConsommable,
} from '../config/metiersData';

interface CalculateurConsommablesUniverselProps {
  open: boolean;
  onClose: () => void;
  quantite: number; // Peut être une surface (m²) pour carreleur, longueur (ml) pour plombier/élec, ou nombre de pièces
  onAjouterConsommables: (consommables: ConsommablesCalcules) => void;
  designation?: string;
  metier: CorpsMetier;
  tailleReference?: string; // Taille carrelage, diamètre tuyau, section câble
}

// Configuration des consommables par type de pièce - CARRELAGE (existant)
type TypePieceCarrelage =
  | 'piece_seche'
  | 'cuisine'
  | 'salle_de_bain'
  | 'douche'
  | 'wc'
  | 'balcon_terrasse'
  | 'escalier'
  | 'general';

interface ConfigConsommableCarrelage {
  nom: string;
  baseKgParM2: number;
  prixUnitaire: number;
  obligatoire: boolean;
}

const CONSOMMABLES_PAR_PIECE_CARRELAGE: Record<TypePieceCarrelage, ConfigConsommableCarrelage[]> = {
  piece_seche: [
    { nom: 'Mortier-colle C1/C2', baseKgParM2: 5, prixUnitaire: 2.5, obligatoire: true },
    { nom: 'Primaire d\'accrochage', baseKgParM2: 0.2, prixUnitaire: 8, obligatoire: true },
    { nom: 'Joint ciment', baseKgParM2: 0.5, prixUnitaire: 4.5, obligatoire: true },
  ],
  cuisine: [
    { nom: 'Mortier-colle C2', baseKgParM2: 5, prixUnitaire: 3, obligatoire: true },
    { nom: 'Primaire d\'adhérence', baseKgParM2: 0.2, prixUnitaire: 8, obligatoire: true },
    { nom: 'Joint ciment hydrofuge', baseKgParM2: 0.5, prixUnitaire: 5, obligatoire: true },
  ],
  salle_de_bain: [
    { nom: 'Mortier-colle C2', baseKgParM2: 5, prixUnitaire: 3, obligatoire: true },
    { nom: 'Primaire d\'accrochage', baseKgParM2: 0.2, prixUnitaire: 8, obligatoire: true },
    { nom: 'SPEC (étanchéité)', baseKgParM2: 1.5, prixUnitaire: 12, obligatoire: true },
    { nom: 'Joint hydrofuge', baseKgParM2: 0.5, prixUnitaire: 5, obligatoire: true },
  ],
  douche: [
    { nom: 'Mortier-colle C2S1', baseKgParM2: 5, prixUnitaire: 3.5, obligatoire: true },
    { nom: 'Primaire d\'accrochage', baseKgParM2: 0.2, prixUnitaire: 8, obligatoire: true },
    { nom: 'SPEC complet', baseKgParM2: 2, prixUnitaire: 15, obligatoire: true },
    { nom: 'Joint époxy', baseKgParM2: 0.6, prixUnitaire: 18, obligatoire: true },
  ],
  wc: [
    { nom: 'Mortier-colle C1/C2', baseKgParM2: 5, prixUnitaire: 2.5, obligatoire: true },
    { nom: 'Primaire (si support ancien)', baseKgParM2: 0.2, prixUnitaire: 8, obligatoire: true },
    { nom: 'Joint hydrofuge', baseKgParM2: 0.5, prixUnitaire: 5, obligatoire: true },
  ],
  balcon_terrasse: [
    { nom: 'Mortier-colle C2S1 extérieur', baseKgParM2: 6, prixUnitaire: 4, obligatoire: true },
    { nom: 'Étanchéité sous carrelage', baseKgParM2: 2, prixUnitaire: 15, obligatoire: true },
    { nom: 'Joint extérieur hydrofuge', baseKgParM2: 0.6, prixUnitaire: 6, obligatoire: true },
  ],
  escalier: [
    { nom: 'Mortier-colle C2', baseKgParM2: 6, prixUnitaire: 3, obligatoire: true },
    { nom: 'Joint hydrofuge', baseKgParM2: 0.5, prixUnitaire: 5, obligatoire: true },
  ],
  general: [
    { nom: 'Mortier-colle', baseKgParM2: 5, prixUnitaire: 2.5, obligatoire: true },
    { nom: 'Joint carrelage', baseKgParM2: 0.5, prixUnitaire: 4.5, obligatoire: true },
  ],
};

/**
 * Calcule le coefficient de consommation en fonction de la taille du carrelage
 */
const calculerCoefficientTaille = (tailleCarrelage?: string): { colle: number; joint: number } => {
  if (!tailleCarrelage) return { colle: 1, joint: 1 };

  const dimensions = tailleCarrelage.split('x').map(d => parseFloat(d));
  if (dimensions.length !== 2 || dimensions.some(isNaN)) {
    return { colle: 1, joint: 1 };
  }

  const [longueur, largeur] = dimensions;
  const surfaceCarreau = longueur * largeur;

  let coefficientColle = 1;
  if (surfaceCarreau < 400) {
    coefficientColle = 1.3;
  } else if (surfaceCarreau < 900) {
    coefficientColle = 1.1;
  } else if (surfaceCarreau < 2500) {
    coefficientColle = 1.0;
  } else if (surfaceCarreau < 4900) {
    coefficientColle = 1.1;
  } else {
    coefficientColle = 1.2;
  }

  let coefficientJoint = 1;
  if (surfaceCarreau < 400) {
    coefficientJoint = 1.5;
  } else if (surfaceCarreau < 900) {
    coefficientJoint = 1.3;
  } else if (surfaceCarreau < 2500) {
    coefficientJoint = 1.0;
  } else if (surfaceCarreau < 4900) {
    coefficientJoint = 0.8;
  } else {
    coefficientJoint = 0.6;
  }

  return { colle: coefficientColle, joint: coefficientJoint };
};

/**
 * Détecte le type de pièce pour CARRELAGE
 */
const detecterTypePieceCarrelage = (designation?: string): TypePieceCarrelage => {
  if (!designation) return 'general';
  const designationLower = designation.toLowerCase();

  if (designationLower.includes('douche') || designationLower.includes('italienne')) {
    return 'douche';
  }
  if (designationLower.includes('salle de bain') || designationLower.includes('sdb') || designationLower.includes('baignoire')) {
    return 'salle_de_bain';
  }
  if (designationLower.includes('cuisine') || designationLower.includes('crédence')) {
    return 'cuisine';
  }
  if (designationLower.includes('wc') || designationLower.includes('toilettes') || designationLower.includes('toilette')) {
    return 'wc';
  }
  if (designationLower.includes('balcon') || designationLower.includes('terrasse') || designationLower.includes('extérieur') || designationLower.includes('exterieur')) {
    return 'balcon_terrasse';
  }
  if (designationLower.includes('escalier') || designationLower.includes('marche')) {
    return 'escalier';
  }
  if (designationLower.includes('séjour') || designationLower.includes('sejour') || designationLower.includes('chambre') ||
      designationLower.includes('bureau') || designationLower.includes('couloir') || designationLower.includes('salon') ||
      designationLower.includes('entrée') || designationLower.includes('entree') || designationLower.includes('interieur') ||
      designationLower.includes('intérieur') || designationLower.includes('garage')) {
    return 'piece_seche';
  }

  return 'general';
};

/**
 * Détecte le type de pièce pour PLOMBERIE
 */
const detecterTypePiecePlomberie = (designation?: string): TypePiecePlomberie => {
  if (!designation) return 'general';
  const designationLower = designation.toLowerCase();

  if (designationLower.includes('salle de bain') || designationLower.includes('sdb') ||
      designationLower.includes('douche') || designationLower.includes('baignoire') ||
      designationLower.includes('lavabo')) {
    return 'salle_de_bain';
  }
  if (designationLower.includes('cuisine') || designationLower.includes('évier') || designationLower.includes('evier')) {
    return 'cuisine';
  }
  if (designationLower.includes('wc') || designationLower.includes('toilettes') || designationLower.includes('toilette')) {
    return 'wc';
  }
  if (designationLower.includes('buanderie') || designationLower.includes('machine à laver') ||
      designationLower.includes('lave-linge')) {
    return 'buanderie';
  }
  if (designationLower.includes('extérieur') || designationLower.includes('exterieur') ||
      designationLower.includes('jardin') || designationLower.includes('robinet extérieur')) {
    return 'exterieur';
  }
  if (designationLower.includes('chauffage') || designationLower.includes('radiateur') ||
      designationLower.includes('chaudière') || designationLower.includes('chauffe-eau')) {
    return 'chauffage';
  }

  return 'general';
};

/**
 * Détecte le type de pièce pour ÉLECTRICITÉ
 */
const detecterTypePieceElectricite = (designation?: string): TypePieceElectricite => {
  if (!designation) return 'general';
  const designationLower = designation.toLowerCase();

  if (designationLower.includes('séjour') || designationLower.includes('sejour') ||
      designationLower.includes('salon') || designationLower.includes('salle à manger')) {
    return 'piece_vie';
  }
  if (designationLower.includes('chambre') || designationLower.includes('bureau')) {
    return 'chambre';
  }
  if (designationLower.includes('cuisine')) {
    return 'cuisine';
  }
  if (designationLower.includes('salle de bain') || designationLower.includes('sdb') ||
      designationLower.includes('douche')) {
    return 'salle_de_bain';
  }
  if (designationLower.includes('extérieur') || designationLower.includes('exterieur') ||
      designationLower.includes('jardin') || designationLower.includes('terrasse')) {
    return 'exterieur';
  }
  if (designationLower.includes('garage') || designationLower.includes('atelier')) {
    return 'garage';
  }
  if (designationLower.includes('tableau') || designationLower.includes('disjoncteur') ||
      designationLower.includes('différentiel')) {
    return 'tableau';
  }

  return 'general';
};

/**
 * Détecte le type d'ouvrage pour MAÇONNERIE
 */
const detecterTypePieceMaconnerie = (designation?: string): TypePieceMaconnerie => {
  if (!designation) return 'general';
  const designationLower = designation.toLowerCase();

  if (designationLower.includes('fondation') || designationLower.includes('semelle') ||
      designationLower.includes('fouille') || designationLower.includes('radier')) {
    return 'fondation';
  }
  if (designationLower.includes('mur porteur') || designationLower.includes('parpaing 20') ||
      designationLower.includes('parpaing 25') || designationLower.includes('élévation')) {
    return 'mur_porteur';
  }
  if (designationLower.includes('cloison') || designationLower.includes('béton cellulaire') ||
      designationLower.includes('alvéolaire')) {
    return 'cloison';
  }
  if (designationLower.includes('dalle intérieur') || designationLower.includes('dalle interieur') ||
      designationLower.includes('chape')) {
    return 'dalle_interieur';
  }
  if (designationLower.includes('dalle extérieur') || designationLower.includes('dalle exterieur') ||
      designationLower.includes('terrasse') || designationLower.includes('garage')) {
    return 'dalle_exterieur';
  }
  if (designationLower.includes('escalier')) {
    return 'escalier';
  }
  if (designationLower.includes('terrasse béton') || designationLower.includes('terrasse beton') ||
      designationLower.includes('désactivé') || designationLower.includes('desactive')) {
    return 'terrasse';
  }
  if (designationLower.includes('extension') || designationLower.includes('agrandissement')) {
    return 'extension';
  }

  return 'general';
};

const CalculateurConsommablesUniversel: React.FC<CalculateurConsommablesUniverselProps> = ({
  open,
  onClose,
  quantite,
  onAjouterConsommables,
  designation,
  metier,
  tailleReference,
}) => {
  const [consommablesListe, setConsommablesListe] = useState<Array<{
    nom: string;
    baseQuantiteParUnite: number;
    prixUnitaire: number;
    unite: string;
    quantite: number;
    total: number;
  }>>([]);

  const [typePiece, setTypePiece] = useState<string>('general');
  const [coefficients, setCoefficients] = useState<{ colle: number; joint: number }>({ colle: 1, joint: 1 });
  const [uniteQuantite, setUniteQuantite] = useState<string>('m²');

  // Fonction pour calculer les consommables - CARRELAGE
  const calculerConsommablesCarrelage = React.useCallback((configs: ConfigConsommableCarrelage[], coeff: { colle: number; joint: number }) => {
    const liste = configs.map(config => {
      let baseAjustee = config.baseKgParM2;

      if (config.nom.toLowerCase().includes('colle') || config.nom.toLowerCase().includes('mortier')) {
        baseAjustee *= coeff.colle;
      } else if (config.nom.toLowerCase().includes('joint')) {
        baseAjustee *= coeff.joint;
      }

      const quantiteCalculee = Math.ceil(quantite * baseAjustee * 10) / 10;
      const total = quantiteCalculee * config.prixUnitaire;

      return {
        nom: config.nom,
        baseQuantiteParUnite: baseAjustee,
        prixUnitaire: config.prixUnitaire,
        unite: 'kg',
        quantite: quantiteCalculee,
        total,
      };
    });

    setConsommablesListe(liste);
  }, [quantite]);

  // Fonction pour calculer les consommables - PLOMBERIE / ÉLECTRICITÉ
  const calculerConsommablesAutres = React.useCallback((configs: ConfigConsommable[]) => {
    const liste = configs.map(config => {
      const quantiteCalculee = Math.ceil(quantite * config.baseQuantiteParUnite * 10) / 10;
      const total = quantiteCalculee * config.prixUnitaire;

      return {
        nom: config.nom,
        baseQuantiteParUnite: config.baseQuantiteParUnite,
        prixUnitaire: config.prixUnitaire,
        unite: config.unite,
        quantite: quantiteCalculee,
        total,
      };
    });

    setConsommablesListe(liste);
  }, [quantite]);

  // Initialiser les consommables selon le métier
  useEffect(() => {
    if (!open) return;

    if (metier === CorpsMetier.CARRELEUR) {
      setUniteQuantite('m²');
      const type = detecterTypePieceCarrelage(designation);
      setTypePiece(type);

      const coeff = calculerCoefficientTaille(tailleReference);
      setCoefficients(coeff);

      const configConsommables = CONSOMMABLES_PAR_PIECE_CARRELAGE[type as TypePieceCarrelage];
      calculerConsommablesCarrelage(configConsommables, coeff);
    }
    else if (metier === CorpsMetier.PLOMBIER) {
      setUniteQuantite('point');
      const type = detecterTypePiecePlomberie(designation);
      setTypePiece(type);

      const configConsommables = CONSOMMABLES_PLOMBERIE[type as TypePiecePlomberie];
      calculerConsommablesAutres(configConsommables);
    }
    else if (metier === CorpsMetier.ELECTRICIEN) {
      setUniteQuantite('point');
      const type = detecterTypePieceElectricite(designation);
      setTypePiece(type);

      const configConsommables = CONSOMMABLES_ELECTRICITE[type as TypePieceElectricite];
      calculerConsommablesAutres(configConsommables);
    }
    else if (metier === CorpsMetier.MACON) {
      setUniteQuantite('m² / ml');
      const type = detecterTypePieceMaconnerie(designation);
      setTypePiece(type);

      const configConsommables = CONSOMMABLES_MACONNERIE[type as TypePieceMaconnerie];
      calculerConsommablesAutres(configConsommables);
    }
  }, [open, designation, quantite, tailleReference, metier, calculerConsommablesCarrelage, calculerConsommablesAutres]);

  const updateConsommable = (index: number, field: 'baseQuantiteParUnite' | 'prixUnitaire', value: number) => {
    setConsommablesListe(prev => {
      const newListe = [...prev];
      newListe[index] = {
        ...newListe[index],
        [field]: value,
        quantite: field === 'baseQuantiteParUnite'
          ? Math.ceil(quantite * value * 10) / 10
          : newListe[index].quantite,
      };
      newListe[index].total = newListe[index].quantite * newListe[index].prixUnitaire;
      return newListe;
    });
  };

  const handleAjouter = () => {
    if (consommablesListe.length > 0) {
      const result: ConsommablesCalcules = {
        consommables: consommablesListe.map(c => ({
          nom: c.nom,
          quantite: c.quantite,
          prixUnitaire: c.prixUnitaire,
          total: c.total,
          unite: c.unite,
        })),
      };
      onAjouterConsommables(result);
      onClose();
    }
  };

  const handleIgnorer = () => {
    onClose();
  };

  const getNomPiece = (type: string): string => {
    const nomsCarrelage: Record<string, string> = {
      piece_seche: 'Pièce sèche',
      cuisine: 'Cuisine',
      salle_de_bain: 'Salle de bain',
      douche: 'Douche',
      wc: 'WC',
      balcon_terrasse: 'Balcon/Terrasse',
      escalier: 'Escalier',
      general: 'Général',
    };

    const nomsPlomberie: Record<string, string> = {
      salle_de_bain: 'Salle de bain',
      cuisine: 'Cuisine',
      wc: 'WC',
      buanderie: 'Buanderie',
      exterieur: 'Extérieur',
      chauffage: 'Chauffage',
      general: 'Général',
    };

    const nomsElectricite: Record<string, string> = {
      piece_vie: 'Pièce de vie',
      chambre: 'Chambre',
      cuisine: 'Cuisine',
      salle_de_bain: 'Salle de bain',
      exterieur: 'Extérieur',
      garage: 'Garage',
      tableau: 'Tableau électrique',
      general: 'Général',
    };

    if (metier === CorpsMetier.CARRELEUR) return nomsCarrelage[type] || type;
    if (metier === CorpsMetier.PLOMBIER) return nomsPlomberie[type] || type;
    if (metier === CorpsMetier.ELECTRICIEN) return nomsElectricite[type] || type;

    return type;
  };

  const getMetierLabel = () => {
    switch (metier) {
      case CorpsMetier.CARRELEUR:
        return 'Carrelage';
      case CorpsMetier.PLOMBIER:
        return 'Plomberie';
      case CorpsMetier.ELECTRICIEN:
        return 'Électricité';
      default:
        return 'Général';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: '#FFFFFF',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: '#374151',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalculateOutlined sx={{ fontSize: 28, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Calculateur {getMetierLabel()}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 4 }, bgcolor: 'white' }}>
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          <strong>{getNomPiece(typePiece)}</strong> - Quantité: <strong>{quantite.toFixed(2)} {uniteQuantite}</strong>
          {tailleReference && metier === CorpsMetier.CARRELEUR && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9, fontWeight: 600 }}>
              Taille carrelage: <strong>{tailleReference} cm</strong>
              {(coefficients.colle !== 1 || coefficients.joint !== 1) && (
                <span style={{ marginLeft: 8 }}>
                  (Colle: ×{coefficients.colle.toFixed(1)} | Joint: ×{coefficients.joint.toFixed(1)})
                </span>
              )}
            </Typography>
          )}
          {tailleReference && metier === CorpsMetier.PLOMBIER && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9, fontWeight: 600 }}>
              Référence: <strong>{tailleReference}</strong>
            </Typography>
          )}
          {tailleReference && metier === CorpsMetier.ELECTRICIEN && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.9, fontWeight: 600 }}>
              Section: <strong>{tailleReference}</strong>
            </Typography>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.8 }}>
            Fournitures calculées automatiquement selon le type de travaux
          </Typography>
        </Alert>

        {consommablesListe.length > 0 && (
          <Box>
            {consommablesListe.map((consommable, index) => {
              const colors = [
                { bg: '#FEF3C7', border: '#F59E0B', text: '#D97706' },
                { bg: '#F3F4F6', border: '#6B7280', text: '#374151' },
                { bg: '#DBEAFE', border: '#3B82F6', text: '#1E40AF' },
                { bg: '#D1FAE5', border: '#10B981', text: '#059669' },
                { bg: '#FEE2E2', border: '#EF4444', text: '#DC2626' },
              ];
              const color = colors[index % colors.length];

              return (
                <Box
                  key={index}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    bgcolor: color.bg,
                    borderRadius: 1.5,
                    border: `2px solid ${color.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: color.text, flex: 1, fontSize: '0.9rem' }}>
                      {consommable.nom}
                    </Typography>
                  </Box>

                  <Grid container spacing={0.5} alignItems="center">
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Base"
                        type="number"
                        value={consommable.baseQuantiteParUnite}
                        onChange={(e) => updateConsommable(index, 'baseQuantiteParUnite', Number(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>/{uniteQuantite}</InputAdornment>,
                        }}
                        sx={{ '& input': { fontSize: '0.8rem', py: 0.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Prix"
                        type="number"
                        value={consommable.prixUnitaire}
                        onChange={(e) => updateConsommable(index, 'prixUnitaire', Number(e.target.value) || 0)}
                        inputProps={{ min: 0, step: 0.1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end" sx={{ fontSize: '0.7rem' }}>€/{consommable.unite}</InputAdornment>,
                        }}
                        sx={{ '& input': { fontSize: '0.8rem', py: 0.5 } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                          Quantité
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: color.text, fontSize: '1rem' }}>
                          {consommable.quantite} {consommable.unite}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ textAlign: 'center', py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                          Total HT
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: color.text, fontSize: '1rem' }}>
                          {consommable.total.toFixed(2)} €
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>
        )}

        <Alert severity="success" sx={{ mt: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          💡 <strong>Astuce :</strong> Ces fournitures seront ajoutées comme sous-lignes au devis.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #e0e0e0' }}>
        <Button
          variant="outlined"
          size="large"
          onClick={handleIgnorer}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderColor: '#9e9e9e',
            color: '#666',
            '&:hover': {
              borderColor: '#666',
              bgcolor: '#f5f5f5',
            },
          }}
        >
          Ignorer
        </Button>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddCircleOutlineOutlined />}
          onClick={handleAjouter}
          sx={{
            px: 4,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            bgcolor: '#F59E0B',
            '&:hover': {
              bgcolor: '#D97706',
            },
          }}
        >
          Ajouter au devis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalculateurConsommablesUniversel;
